/**
 * Layout constants.
 *
 * Every one of these is currently a magic number declared inside `autoLayout()` and again
 * inside `computeDisplayLayout()`, which is how the two drifted into being near-copies of
 * each other in the first place.
 */

export interface LayoutConstants {
  /** Horizontal distance between depth columns, per side. */
  COLW: number;
  /** Fallback height when a node has not been measured. Also the root's assumed height. */
  ROOTH: number;
  /** Vertical gap between siblings. */
  VGAP: number;
  /** Extra vertical gap between top-level branches. */
  BRANCHGAP: number;
}

export const LAYOUT: LayoutConstants = {
  COLW: 250,
  ROOTH: 46,
  VGAP: 16,
  BRANCHGAP: 16,
};

/** Collision resolution. */
export const COLLISION = {
  PADX: 18,
  PADY: 12,
  /** Upper bound on separation passes. The loop stops early once nothing moves. */
  MAXPASS: 60,
  /** Box size assumed for an unmeasured node. Deliberately not ROOTH — the original differs. */
  FALLBACK_W: 160,
  FALLBACK_H: 44,
} as const;

/**
 * Height of one documentation row in an expanded node, and the gap between rows.
 *
 * ⚠️ ROW_H MUST EQUAL `.beanrow`'s CSS height. It is the coupling CLAUDE.md warns about:
 * `docExtraHeight()` reserves exactly this much space per row, and if the two disagree the
 * expanded layout desynchronises silently — nodes overlap and wires miss their anchors, with
 * nothing erroring.
 *
 * The plan asks for this to become "one exported constant so the two can no longer
 * desynchronise". CSS cannot import a TypeScript module, so the constant cannot literally be
 * shared while the vanilla stylesheet exists. Instead `layout/docHeight.test.ts` READS the
 * stylesheet and fails if it drifts — which is the enforceable half of the same idea, and
 * survives until the CSS moves into the React client and can consume this directly.
 */
export const ROW_H = 22;
export const ROW_GAP = 3;

/** Custom docs collapse past this many into a "+N" row. */
export const CUSTOM_DOC_ROWS_SHOWN = 2;
